import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// FullAdmin-only: database backup info and manual controls. Reachable from
// the admin nav (see MainAdminDashboard.js — only shown to FullAdmins,
// mirroring Schools.js/GlobalUsers.js). The database itself isn't
// school-scoped, so this lives in the global /admin/main area rather than
// under any :schoolId.
function Backup() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [backingUp, setBackingUp] = useState(false);
  const [backupResult, setBackupResult] = useState(null);

  const fetchInfo = useCallback(async () => {
    try {
      const response = await axios.get('/api/admin/backup');
      setInfo(response.data);
    } catch (err) {
      setError('Failed to load backup info');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInfo();
  }, [fetchInfo]);

  // Triggers an immediate Google Drive backup (see
  // backend/services/drivebackup.go's RunForced) — unlike the automatic
  // periodic backup, this always produces a fresh upload even if nothing's
  // changed, since clicking the button is an explicit request for a
  // receipt, not just an early check.
  const handleBackupNow = async () => {
    setBackingUp(true);
    setBackupResult(null);
    try {
      const response = await axios.post('/api/admin/backup/drive');
      setBackupResult({ success: true, message: response.data.message });
    } catch (err) {
      setBackupResult({
        success: false,
        message: err.response?.data?.error || 'Backup failed'
      });
    } finally {
      setBackingUp(false);
    }
  };

  const handleDownload = () => {
    window.location.href = '/api/admin/backup/download';
  };

  if (loading) {
    return <div>Loading backup info...</div>;
  }

  return (
    <div>
      <h2>Database Backup</h2>

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      <div style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '1.5rem',
        maxWidth: '500px'
      }}>
        {info && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div><strong>Database file:</strong> {info.filename}</div>
            <div><strong>Size:</strong> {(info.size_bytes / 1024).toFixed(1)} KB</div>
            <div>
              <strong>Last modified:</strong>{' '}
              {new Date(info.last_modified * 1000).toLocaleString()}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            onClick={handleBackupNow}
            disabled={backingUp}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: backingUp ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: backingUp ? 'not-allowed' : 'pointer'
            }}
          >
            {backingUp ? 'Backing up…' : '☁️ Back Up to Drive Now'}
          </button>

          <button
            onClick={handleDownload}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ⬇️ Download Database File
          </button>
        </div>

        {backupResult && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem 1rem',
            borderRadius: '4px',
            backgroundColor: backupResult.success ? '#d4edda' : '#f8d7da',
            color: backupResult.success ? '#155724' : '#721c24'
          }}>
            {backupResult.success ? '✅ ' : '❌ '}{backupResult.message}
          </div>
        )}

        <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '1rem' }}>
          Automatic backups to Google Drive run every few hours whenever the
          database has changed. Use "Back Up to Drive Now" to force an immediate
          backup regardless — useful right before a risky change, or to confirm
          Drive backups are working. If Drive backup isn't configured on this
          server, this will report an error rather than silently doing nothing.
        </p>
      </div>
    </div>
  );
}

export default Backup;
